// RITA-1547 — Die gespeicherten Daten des Beraters für die Bearbeiten-Seite.
//
// Die Karte aus RITA-1543 rechnet die Platzhalterwerte aus den Formularwerten der Beraterseite, die bei
// gesperrtem Knopf dem gespeicherten Stand entsprechen. Die eigene Bearbeiten-Seite hat diese Maske nicht;
// sie lädt deshalb denselben gespeicherten Stand (getAgentv2 wie AgentEditPage) und dieselben
// Stammdatenlisten (DataContext) und rechnet mit denselben Funktionen. So entstehen auf beiden Wegen
// dieselben Werte.
import { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import DataContext from '../../../context/DataProvider/DataContext';
import getAgentById from '../../../Apollo/queries/getAgentById';
import {
  StammdatenBezug,
  stammdatenKontext,
  VertragWertekontext,
  vollerName,
} from '../services/vertragPlatzhalterwerte';

export interface BeraterVertragswerte {
  laedt: boolean;
  fehler: boolean;
  /** Gespeicherter Stand des Beraters; null, solange er (oder die Stammdaten) noch laden. */
  berater: StammdatenBezug | null;
  beraterName: string;
  kontext: Omit<VertragWertekontext, 'bearbeiter' | 'heute'> | null;
  erneutLaden: () => void;
}

interface Antwort {
  getAgentv2: (StammdatenBezug & { agent_id: number; mandateReference?: string | null }) | null;
}

export default function useBeraterVertragswerte(agentId: number): BeraterVertragswerte {
  const { brachOffices, country, status, rank, level, contractType, agents, loaderFun } = useContext(DataContext);
  const { data, loading, error, refetch } = useQuery<Antwort>(getAgentById, {
    variables: { agent_id: agentId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    // Wie AgentEditPage: die Stammdatenlisten der Beraterseite anstoßen.
    loaderFun.loadStatus();
    loaderFun.loadRank();
    loaderFun.loadLevel();
    loaderFun.loadBranchOffice();
    loaderFun.loadContractType();
    loaderFun.loadCountry();
  }, []);

  const gespeichert = data?.getAgentv2 ?? null;
  const berater = useMemo<StammdatenBezug | null>(
    () =>
      gespeichert && Number(gespeichert.agent_id) === agentId
        ? { ...gespeichert, mandateReference: gespeichert.mandateReference ?? '' }
        : null,
    [gespeichert, agentId],
  );
  // Ohne geladene Listen würden Firma, Vertragsart, Teamleiter usw. leer eingesetzt. Gewartet wird nur auf
  // Listen, auf die der Berater verweist: Eine im PRM leere Liste (etwa keine Stufen) hielte die Seite
  // sonst für immer beim Laden.
  const verweise: Array<[unknown, readonly unknown[]]> = berater
    ? [
        [berater.branch_office_id, brachOffices],
        [berater.country_id, country],
        [berater.country2_id, country],
        [berater.status_id, status],
        [berater.rank_id, rank],
        [berater.level_id, level],
        [berater.contract_type_id, contractType],
        [berater.manager_id, agents],
      ]
    : [];
  const listenBereit = verweise.every(
    ([id, liste]) => id === null || id === undefined || id === '' || liste.length > 0,
  );
  const kontext = useMemo(
    () =>
      berater && listenBereit
        ? stammdatenKontext(berater, {
            firmen: brachOffices,
            laender: country,
            status,
            raenge: rank,
            level,
            vertragsarten: contractType,
            berater: agents,
          })
        : null,
    [berater, listenBereit, brachOffices, country, status, rank, level, contractType, agents],
  );

  return {
    laedt: !error && (loading || !berater || !kontext),
    fehler: !!error || (!loading && !!data && !gespeichert),
    berater: kontext ? berater : null,
    beraterName: berater ? vollerName(berater.name, berater.surname) : '',
    kontext,
    erneutLaden: () => {
      refetch().catch(() => undefined);
    },
  };
}
